import { useState, useRef, useEffect } from "react";
import { apiFetch } from "./utils/api";

const S = {
  page: { minHeight: "100vh", background: "linear-gradient(135deg,#0D1B2A 0%,#1A2F45 100%)", display: "flex", alignItems: "center", justifyContent: "center", padding: "20px" },
  card: { background: "#fff", borderRadius: 20, padding: "40px 38px", width: 420, boxShadow: "0 24px 80px rgba(0,0,0,.35)" },
  heading: { fontFamily: "'Sora',sans-serif", fontWeight: 700, fontSize: 17, color: "#0D1B2A", marginBottom: 4 },
  subhead: { fontSize: 12, color: "#64748B", marginBottom: 22, lineHeight: 1.5 },
  label: { display: "block", fontSize: 12, fontWeight: 600, color: "#374151", marginBottom: 6 },
  err: { marginBottom: 14, padding: "10px 14px", background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: 10, fontSize: 12, color: "#DC2626", display: "flex", alignItems: "flex-start", gap: 7 },
  ok: { marginBottom: 14, padding: "10px 14px", background: "#F0FDF4", border: "1px solid #BBF7D0", borderRadius: 10, fontSize: 12, color: "#166534", display: "flex", alignItems: "center", gap: 7 },
  divider: { display: "flex", alignItems: "center", gap: 10, margin: "18px 0" },
  divLine: { flex: 1, height: 1, background: "#E2E8F0" },
  divText: { fontSize: 11, color: "#94A3B8", fontWeight: 600 },
  link: { background: "none", border: "none", color: "#E8520A", fontSize: 12, fontWeight: 600, cursor: "pointer", padding: 0, textDecoration: "underline" },
  footer: { textAlign: "center", marginTop: 22, fontSize: 11, color: "#94A3B8" },
};

function Spinner() {
  return <div style={{ width: 15, height: 15, border: "2.5px solid rgba(255,255,255,.3)", borderTopColor: "#fff", borderRadius: "50%", animation: "spin .8s linear infinite" }} />;
}

// SVG eye icons — no emoji, no external library
const EyeIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);
const EyeOffIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94" />
    <path d="M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19" />
    <line x1="1" y1="1" x2="23" y2="23" />
  </svg>
);

// ── OTP Boxes — single hidden input behind 6 visual cells ─────────────────────
// This is the most reliable approach for paste: one real <input> captures
// ALL keyboard and clipboard events. The 6 cells are pure display divs.
function OtpBoxes({ value, onChange }) {
  const inputRef = useRef(null);
  const digits = (value + "      ").slice(0, 6).split("");
  const filled = value.replace(/\s/g, "").length;

  // Focus the hidden input whenever user clicks anywhere in the row
  const focus = () => inputRef.current?.focus();

  const handleInput = (e) => {
    const raw = e.target.value.replace(/\D/g, "").slice(0, 6);
    onChange(raw);
  };

  const handleKey = (e) => {
    if (e.key === "Backspace") {
      e.preventDefault();
      onChange(value.slice(0, -1));
    }
  };

  // Auto-focus on mount
  useEffect(() => { inputRef.current?.focus(); }, []);

  return (
    <div
      onClick={focus}
      style={{ display: "flex", gap: 8, justifyContent: "center", margin: "4px 0 16px", cursor: "text", position: "relative" }}
    >
      {/* Hidden real input — handles ALL keyboard + paste events */}
      <input
        ref={inputRef}
        type="text"
        inputMode="numeric"
        value={value}
        onChange={handleInput}
        onKeyDown={handleKey}
        autoComplete="one-time-code"
        style={{
          position: "absolute",
          opacity: 0,
          width: 1,
          height: 1,
          pointerEvents: "none",
          top: 0, left: 0,
        }}
      />

      {/* 6 visual cells — clicking any focuses the hidden input */}
      {digits.map((d, i) => {
        const isFocusedCell = i === Math.min(filled, 5);
        const isDone = d.trim() !== "";
        return (
          <div
            key={i}
            style={{
              width: 46, height: 52,
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 22, fontWeight: 700, fontFamily: "'Courier New',monospace",
              border: "2px solid",
              borderColor: isDone ? "#10B981" : isFocusedCell ? "#E8520A" : "#E2E8F0",
              borderRadius: 10,
              color: "#0D1B2A",
              background: isDone ? "#F0FDF4" : "#F8FAFC",
              boxShadow: isFocusedCell && !isDone ? "0 0 0 3px rgba(232,82,10,.12)" : "none",
              transition: "all .15s",
              userSelect: "none",
            }}
          >
            {d.trim() || ""}
          </div>
        );
      })}
    </div>
  );
}

// ── Password Login ─────────────────────────────────────────────────────────────
function PasswordLogin({ onLogin, onSwitchOtp, onForgot }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handle = async () => {
    if (!email.trim()) { setError("Email is required."); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { setError("Enter a valid email address."); return; }
    if (!password) { setError("Password is required."); return; }
    setError(""); setLoading(true);
    try {
      const res = await apiFetch("/api/auth/login", { method: "POST", body: JSON.stringify({ email: email.trim().toLowerCase(), password }) });
      const data = await res.json();
      if (!data.success) { setError(data.message || "Login failed."); return; }
      localStorage.setItem("epr_token", data.token);
      onLogin(data.token, data.user);
    } catch { setError("Network error. Please try again."); }
    finally { setLoading(false); }
  };

  return (
    <>
      <div style={S.heading}>Sign In</div>
      <div style={S.subhead}>Enter your admin credentials to continue.</div>
      {error && <div style={S.err}><span>⚠️</span>{error}</div>}
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        <div>
          <label style={S.label}>Email Address</label>
          <input className="inp" type="email" placeholder="connect@dolluzcorp.com" value={email}
            onChange={e => { setEmail(e.target.value); setError(""); }}
            onKeyDown={e => e.key === "Enter" && handle()}
            style={{ width: "100%" }} />
        </div>
        <div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
            <label style={{ ...S.label, marginBottom: 0 }}>Password</label>
            <button style={S.link} onClick={onForgot}>Forgot password?</button>
          </div>
          <div style={{ position: "relative" }}>
            <input className="inp" type={showPwd ? "text" : "password"} placeholder="••••••••" value={password}
              onChange={e => { setPassword(e.target.value); setError(""); }}
              onPaste={e => { e.preventDefault(); const pasted = e.clipboardData.getData("text"); setPassword(pasted.trim()); setError(""); }}
              onKeyDown={e => e.key === "Enter" && handle()}
              style={{ width: "100%", paddingRight: 40 }} />
            <button onClick={() => setShowPwd(v => !v)}
              style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "#94A3B8", display: "flex", alignItems: "center" }}>
              {showPwd ? <EyeOffIcon /> : <EyeIcon />}
            </button>
          </div>
        </div>
        <button className="btn-primary" onClick={handle} disabled={loading}
          style={{ marginTop: 4, padding: "12px", justifyContent: "center", fontSize: 14, width: "100%", display: "flex", alignItems: "center", gap: 8 }}>
          {loading ? <><Spinner /> Signing in…</> : "Sign In →"}
        </button>
      </div>
      <div style={S.divider}><div style={S.divLine} /><span style={S.divText}>OR</span><div style={S.divLine} /></div>
      <button onClick={onSwitchOtp}
        style={{ width: "100%", padding: "11px", borderRadius: 10, border: "1.5px solid #E2E8F0", background: "#F8FAFC", fontSize: 13, fontWeight: 600, color: "#475569", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 8, transition: "all .15s" }}
        onMouseEnter={e => { e.currentTarget.style.borderColor = "#E8520A"; e.currentTarget.style.color = "#E8520A"; }}
        onMouseLeave={e => { e.currentTarget.style.borderColor = "#E2E8F0"; e.currentTarget.style.color = "#475569"; }}>
        Login with OTP instead
      </button>
    </>
  );
}

// ── OTP Login ──────────────────────────────────────────────────────────────────
function OtpLogin({ onLogin, onBack }) {
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [devOtp, setDevOtp] = useState("");

  const requestOtp = async () => {
    if (!email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { setError("Enter a valid email address."); return; }
    setError(""); setLoading(true);
    try {
      const res = await apiFetch("/api/auth/request-otp", { method: "POST", body: JSON.stringify({ email: email.trim().toLowerCase() }) });
      const data = await res.json();
      if (!data.success) { setError(data.message || "Failed to send OTP."); return; }
      if (!data.otpSent) { setError("No active admin account found for this email address."); return; }
      if (data._devOtp) setDevOtp(data._devOtp);
      setStep(2);
    } catch { setError("Network error. Please try again."); }
    finally { setLoading(false); }
  };

  const verifyOtp = async () => {
    if (otp.replace(/\s/g, "").length < 6) { setError("Enter all 6 digits."); return; }
    setError(""); setLoading(true);
    try {
      const res = await apiFetch("/api/auth/verify-otp", { method: "POST", body: JSON.stringify({ email: email.trim().toLowerCase(), otp: otp.trim() }) });
      const data = await res.json();
      if (!data.success) { setError(data.message || "Invalid OTP."); return; }
      localStorage.setItem("epr_token", data.token);
      onLogin(data.token, data.user);
    } catch { setError("Network error. Please try again."); }
    finally { setLoading(false); }
  };

  if (step === 1) return (
    <>
      <button onClick={onBack} style={{ ...S.link, marginBottom: 14, display: "flex", alignItems: "center", gap: 5, textDecoration: "none" }}>← Back to sign in</button>
      <div style={S.heading}>Login with OTP</div>
      <div style={S.subhead}>Enter your admin email. We'll send a 6-digit code.</div>
      {error && <div style={S.err}><span>⚠️</span>{error}</div>}
      <div style={{ marginBottom: 14 }}>
        <label style={S.label}>Admin Email Address</label>
        <input className="inp" type="email" placeholder="connect@dolluzcorp.com" value={email}
          onChange={e => { setEmail(e.target.value); setError(""); }}
          onKeyDown={e => e.key === "Enter" && requestOtp()}
          style={{ width: "100%" }} />
      </div>
      <button className="btn-primary" onClick={requestOtp} disabled={loading}
        style={{ padding: "12px", justifyContent: "center", fontSize: 14, width: "100%", display: "flex", alignItems: "center", gap: 8 }}>
        {loading ? <><Spinner /> Sending…</> : "Send OTP →"}
      </button>
    </>
  );

  return (
    <>
      <button onClick={() => { setStep(1); setOtp(""); setError(""); setDevOtp(""); }}
        style={{ ...S.link, marginBottom: 14, display: "flex", alignItems: "center", gap: 5, textDecoration: "none" }}>← Change email</button>
      <div style={S.heading}>Enter OTP</div>
      <div style={S.subhead}>
        A 6-digit code was sent to <strong>{email.trim().toLowerCase()}</strong>. Expires in 10 minutes.
      </div>
      {error && <div style={S.err}><span>⚠️</span>{error}</div>}
      {devOtp && (
        <div style={{ marginBottom: 12, padding: "8px 12px", background: "#FFFBEB", border: "1px solid #FDE68A", borderRadius: 8, fontSize: 11, color: "#92400E", fontFamily: "monospace" }}>
          [DEV] OTP: <strong>{devOtp}</strong>
        </div>
      )}
      <OtpBoxes value={otp} onChange={setOtp} />
      <button className="btn-primary" onClick={verifyOtp}
        disabled={loading || otp.replace(/\s/g, "").length < 6}
        style={{ padding: "12px", justifyContent: "center", fontSize: 14, width: "100%", display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
        {loading ? <><Spinner /> Verifying…</> : "Verify & Sign In →"}
      </button>
      <div style={{ textAlign: "center", fontSize: 12, color: "#64748B" }}>
        Didn't receive it?{" "}
        <button style={S.link} onClick={() => { setOtp(""); setError(""); setDevOtp(""); requestOtp(); }} disabled={loading}>Resend OTP</button>
      </div>
    </>
  );
}

// ── Forgot Password ────────────────────────────────────────────────────────────
function ForgotPassword({ onBack }) {
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [resetToken, setResetToken] = useState("");
  const [newPass, setNewPass] = useState("");
  const [confirmPass, setConfirmPass] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [devOtp, setDevOtp] = useState("");

  const sendOtp = async () => {
    if (!email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { setError("Enter a valid email address."); return; }
    setError(""); setLoading(true);
    try {
      const res = await apiFetch("/api/auth/forgot-password", { method: "POST", body: JSON.stringify({ email: email.trim().toLowerCase() }) });
      const data = await res.json();
      if (!data.success) { setError(data.message || "Failed to send OTP."); return; }
      if (data._devOtp) setDevOtp(data._devOtp);
      setStep(2);
    } catch { setError("Network error. Please try again."); }
    finally { setLoading(false); }
  };

  const verifyOtp = async () => {
    if (otp.replace(/\s/g, "").length < 6) { setError("Enter all 6 digits."); return; }
    setError(""); setLoading(true);
    try {
      const res = await apiFetch("/api/auth/forgot-password/verify", { method: "POST", body: JSON.stringify({ email: email.trim().toLowerCase(), otp: otp.trim() }) });
      const data = await res.json();
      if (!data.success) { setError(data.message || "OTP verification failed."); return; }
      setResetToken(data.resetToken);
      setStep(3);
    } catch { setError("Network error. Please try again."); }
    finally { setLoading(false); }
  };

  const resetPassword = async () => {
    if (!newPass || newPass.length < 8) { setError("Password must be at least 8 characters."); return; }
    if (newPass !== confirmPass) { setError("Passwords do not match."); return; }
    setError(""); setLoading(true);
    try {
      const res = await apiFetch("/api/auth/forgot-password/reset", { method: "POST", body: JSON.stringify({ email: email.trim().toLowerCase(), resetToken, newPassword: newPass, confirmPassword: confirmPass }) });
      const data = await res.json();
      if (!data.success) { setError(data.message || "Password reset failed."); return; }
      setStep(4);
      setTimeout(onBack, 2500);
    } catch { setError("Network error. Please try again."); }
    finally { setLoading(false); }
  };

  const score = [newPass.length >= 8, /[A-Z]/.test(newPass), /\d/.test(newPass), /[^A-Za-z0-9]/.test(newPass)].filter(Boolean).length;
  const strengthColor = ["#EF4444", "#F59E0B", "#10B981", "#10B981"][score - 1] || "#EF4444";
  const strengthLabel = ["Weak", "Fair", "Good", "Strong"][score - 1] || "Weak";
  const passMatch = confirmPass && newPass === confirmPass;
  const passMismatch = confirmPass && newPass !== confirmPass;

  if (step === 4) return (
    <div style={{ textAlign: "center", padding: "20px 0" }}>
      <div style={{ fontSize: 48, marginBottom: 16 }}>✅</div>
      <div style={S.heading}>Password Reset!</div>
      <div style={{ fontSize: 13, color: "#64748B", marginBottom: 16 }}>You can now sign in with your new password.</div>
      <div style={S.ok}><span>✓</span> Redirecting to sign in…</div>
    </div>
  );

  return (
    <>
      {step === 1
        ? <button onClick={onBack} style={{ ...S.link, marginBottom: 14, display: "flex", alignItems: "center", gap: 5, textDecoration: "none" }}>← Back to sign in</button>
        : <button onClick={() => { setStep(s => s - 1); setError(""); }} style={{ ...S.link, marginBottom: 14, display: "flex", alignItems: "center", gap: 5, textDecoration: "none" }}>← Back</button>
      }
      {/* Progress bar */}
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 20 }}>
        {[1, 2, 3].map(s => <div key={s} style={{ flex: 1, height: 4, borderRadius: 100, background: s <= step ? "#E8520A" : "#E2E8F0", transition: "all .3s" }} />)}
        <span style={{ fontSize: 10, color: "#94A3B8", whiteSpace: "nowrap" }}>Step {step} of 3</span>
      </div>

      {step === 1 && <>
        <div style={S.heading}>Forgot Password</div>
        <div style={S.subhead}>Enter your admin email. We'll send a reset OTP.</div>
        {error && <div style={S.err}><span>⚠️</span>{error}</div>}
        <div style={{ marginBottom: 14 }}>
          <label style={S.label}>Admin Email Address</label>
          <input className="inp" type="email" placeholder="connect@dolluzcorp.com" value={email}
            onChange={e => { setEmail(e.target.value); setError(""); }}
            onKeyDown={e => e.key === "Enter" && sendOtp()}
            style={{ width: "100%" }} />
        </div>
        <button className="btn-primary" onClick={sendOtp} disabled={loading}
          style={{ padding: "12px", justifyContent: "center", fontSize: 14, width: "100%", display: "flex", alignItems: "center", gap: 8 }}>
          {loading ? <><Spinner /> Sending…</> : "Send Reset OTP →"}
        </button>
      </>}

      {step === 2 && <>
        <div style={S.heading}>Enter Reset OTP</div>
        <div style={S.subhead}>A 6-digit code was sent to <strong>{email.trim().toLowerCase()}</strong>. Expires in 10 minutes.</div>
        {error && <div style={S.err}><span>⚠️</span>{error}</div>}
        {devOtp && (
          <div style={{ marginBottom: 12, padding: "8px 12px", background: "#FFFBEB", border: "1px solid #FDE68A", borderRadius: 8, fontSize: 11, color: "#92400E", fontFamily: "monospace" }}>
            [DEV] OTP: <strong>{devOtp}</strong>
          </div>
        )}
        <OtpBoxes value={otp} onChange={setOtp} />
        <button className="btn-primary" onClick={verifyOtp}
          disabled={loading || otp.replace(/\s/g, "").length < 6}
          style={{ padding: "12px", justifyContent: "center", fontSize: 14, width: "100%", display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
          {loading ? <><Spinner /> Verifying…</> : "Verify OTP →"}
        </button>
        <div style={{ textAlign: "center", fontSize: 12, color: "#64748B" }}>
          Didn't receive it?{" "}
          <button style={S.link} onClick={() => { setOtp(""); setError(""); setDevOtp(""); sendOtp(); }} disabled={loading}>Resend</button>
        </div>
      </>}

      {step === 3 && <>
        <div style={S.heading}>Set New Password</div>
        <div style={S.subhead}>Choose a strong password for your account.</div>
        {error && <div style={S.err}><span>⚠️</span>{error}</div>}
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <div>
            <label style={S.label}>New Password</label>
            <div style={{ position: "relative" }}>
              <input className="inp" type={showNew ? "text" : "password"} placeholder="Min 8 characters" value={newPass}
                onChange={e => { setNewPass(e.target.value); setError(""); }}
                style={{ width: "100%", paddingRight: 40 }} />
              <button onClick={() => setShowNew(v => !v)}
                style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "#94A3B8", display: "flex", alignItems: "center" }}>
                {showNew ? <EyeOffIcon /> : <EyeIcon />}
              </button>
            </div>
            {newPass && (
              <div style={{ marginTop: 6, display: "flex", alignItems: "center", gap: 8 }}>
                <div style={{ flex: 1, height: 4, borderRadius: 100, background: "#E2E8F0", overflow: "hidden" }}>
                  <div style={{ width: (score * 25) + "%", height: "100%", background: strengthColor, transition: "width .3s" }} />
                </div>
                <span style={{ fontSize: 10, color: strengthColor, fontWeight: 700, width: 40 }}>{strengthLabel}</span>
              </div>
            )}
          </div>
          <div>
            <label style={S.label}>Confirm New Password</label>
            <div style={{ position: "relative" }}>
              <input className="inp" type={showConfirm ? "text" : "password"} placeholder="Repeat password" value={confirmPass}
                onChange={e => { setConfirmPass(e.target.value); setError(""); }}
                onKeyDown={e => e.key === "Enter" && resetPassword()}
                style={{ width: "100%", paddingRight: 40, borderColor: passMismatch ? "#EF4444" : passMatch ? "#10B981" : "" }} />
              <button onClick={() => setShowConfirm(v => !v)}
                style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "#94A3B8", display: "flex", alignItems: "center" }}>
                {showConfirm ? <EyeOffIcon /> : <EyeIcon />}
              </button>
            </div>
            {passMismatch && <div style={{ fontSize: 11, color: "#EF4444", marginTop: 4 }}>Passwords do not match</div>}
            {passMatch && <div style={{ fontSize: 11, color: "#10B981", marginTop: 4 }}>✓ Passwords match</div>}
          </div>
          <button className="btn-primary" onClick={resetPassword}
            disabled={loading || !newPass || !confirmPass}
            style={{ padding: "12px", justifyContent: "center", fontSize: 14, width: "100%", display: "flex", alignItems: "center", gap: 8, marginTop: 4 }}>
            {loading ? <><Spinner /> Resetting…</> : "Reset Password →"}
          </button>
        </div>
      </>}
    </>
  );
}

// ── Main ───────────────────────────────────────────────────────────────────────
const LoginScreen = ({ onLogin }) => {
  const [mode, setMode] = useState("password");
  return (
    <div style={S.page}>
      <div style={S.card}>
        <div style={{ textAlign: "center", marginBottom: 28 }}>
          <div style={{ fontFamily: "'Sora',sans-serif", fontWeight: 800, fontSize: 26, color: "#0D1B2A", letterSpacing: -1 }}>
            Dolluz Corp<span style={{ color: "#E8520A" }}>.</span>
          </div>
          <div style={{ fontSize: 10, color: "#94A3B8", fontWeight: 700, letterSpacing: 2.5, textTransform: "uppercase", marginTop: 4 }}>
            EPR Admin Portal
          </div>
        </div>
        {mode === "password" && <PasswordLogin onLogin={onLogin} onSwitchOtp={() => setMode("otp")} onForgot={() => setMode("forgot")} />}
        {mode === "otp" && <OtpLogin onLogin={onLogin} onBack={() => setMode("password")} />}
        {mode === "forgot" && <ForgotPassword onBack={() => setMode("password")} />}
        <div style={S.footer}>Employee Performance Review Portal · Dolluz Corp</div>
      </div>
    </div>
  );
};

export default LoginScreen;